<template>
    <span>
        <span @click="start">
            <slot :processing="processing" />
        </span>

        <Modal v-if="confirming" size="md" @close="closeModal">
            <template #header>
                <div class="text-gray-600">{{ title }}</div>
            </template>

            <template #body>
                <div class="mt-6 mb-10 w-full px-6">
                    <template v-if="offerPasskey && !codeSent">
                        <p class="pb-5 text-sm text-gray-500">
                            Use your passkey, or we can email you a 6-digit
                            code.
                        </p>

                        <div class="flex flex-col gap-3">
                            <AtomicButton
                                color="yellow"
                                size="md"
                                class="w-full"
                                :disabled="passkeyLoading"
                                @click="verifyPasskey"
                            >
                                Confirm with passkey
                            </AtomicButton>
                            <p v-if="passkeyError" class="text-xs text-red-600">
                                {{ passkeyError }}
                            </p>
                            <AtomicButton
                                color="white"
                                size="md"
                                class="w-full"
                                :disabled="processing"
                                @click="sendCode"
                            >
                                Email me a code
                            </AtomicButton>
                        </div>
                    </template>

                    <p v-else class="pb-5 text-sm text-gray-500">
                        {{ content }}
                    </p>

                    <OtpInput
                            v-if="codeSent"
                            input-classes="mt-2 rounded-[6px] text-center border border-gray-300 mx-1 sm:mx-2 focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 focus:outline-hidden transition duration-200 ease-in-out"
                            separator=""
                            :num-inputs="6"
                            :should-auto-focus="true"
                            :is-input-num="true"
                            :error="error"
                             @on-complete="onComplete"
                        />
                </div>
            </template>

            <template #footer>
                <div class="flex justify-end border-t border-gray-200 p-6">
                    <AtomicButton color="white" size="md" @click="closeModal">
                        Cancel
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </span>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { route } from 'ziggy-js';
import OtpInput from '@/Pages/Auth/components/OtpInput.vue';
import { usePasskeyVerification } from '@/composables/usePasskeyVerification';

const props = defineProps({
    title: {
        type: String,
        default: 'Verify your identity',
    },
    content: {
        type: String,
        default: 'We sent a 6-digit code to your email address. Please enter it below to continue.',
    },
    /**
     * Offer confirming with a passkey before emailing a code. A passkey only
     * satisfies `password.confirm` routes, not the OTP-only ones (email
     * change, password set).
     */
    allowPasskey: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['confirmed']);

const confirming = ref(false);
const processing = ref(false);
const error = ref('');
const codeSent = ref(false);

const {
    verify: verifyPasskey,
    isLoading: passkeyLoading,
    isSupported: passkeysSupported,
    errorMessage: passkeyError,
} = usePasskeyVerification({
    routes: {
        options: route('passkey.confirm-options'),
        submit: route('passkey.confirm'),
    },
    onSuccess: () => {
        closeModal();
        nextTick(() => emit('confirmed'));
    },
});

const offerPasskey = computed(
    () => props.allowPasskey && passkeysSupported.value
);

const start = () => {
    if (processing.value || confirming.value) {
        return;
    }

    if (!offerPasskey.value) {
        sendCode();
        return;
    }

    // Skip the prompt entirely while a previous confirmation is still fresh
    axios.get(route('password.confirmation')).then((response) => {
        if (response.data.confirmed) {
            emit('confirmed');
        } else {
            confirming.value = true;
        }
    });
};

const sendCode = () => {
    processing.value = true;
    error.value = '';

    axios
        .post(route('user.otp.store'))
        .then(() => {
            processing.value = false;
            codeSent.value = true;
            confirming.value = true;
        })
        .catch((err) => {
            processing.value = false;
            create({
                title: 'Error',
                text: err?.response?.data?.message ?? 'Failed to send verification code. Please try again.',
                type: 'error',
            });
        });
};

const onComplete = (code: string) => {
    processing.value = true;
    error.value = '';

    axios
        .put(route('user.otp.update'), { code })
        .then(() => {
            processing.value = false;
            closeModal();
            nextTick(() => emit('confirmed'));
        })
        .catch((err) => {
            processing.value = false;
            error.value =
                err?.response?.data?.errors?.code?.[0] ??
                'Invalid code. Please try again.';
        });
};

const closeModal = () => {
    confirming.value = false;
    codeSent.value = false;
    error.value = '';
};
</script>
